import { TimeSelect } from "@cmp-src/form/TimeSelect";
import { getNowDate } from "@gettactic/helpers/src/date-time/getNowDate";
import { injectTimeIntoDate } from "@gettactic/helpers/src/date-time/injectTimeIntoDate";
import { dateToTimeString } from "@gettactic/helpers/src/date-time/timeStringConverters";
import { Clock } from "lucide-react";
import React from "react";

export interface StartEndTimeInputsProps {
  startDate: Date | null;
  endDate: Date | null;
  onStartDateChange: (date: Date) => void;
  onEndDateChange: (date: Date) => void;
  onRangeChange: (start: Date, end: Date) => void;
}

const END_BEFORE_START_ERROR = "End time must be after start time";

/** Identifies the range a rejection was made against. */
const rangeKey = (start: Date | null, end: Date | null): string =>
  `${start?.getTime() ?? ""}-${end?.getTime() ?? ""}`;

/**
 * Start and end fields to type an exact time range. A typed time keeps the day of the
 * current value; a range whose end is not after its start is rejected and the field keeps
 * its previous time.
 */
export const StartEndTimeInputs: React.FC<StartEndTimeInputsProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onRangeChange
}) => {
  // The error belongs to the range it was rejected against: once the range changes
  // another way (drag, preset, keyboard) it no longer applies.
  const [rejection, setRejection] = React.useState<{
    field: "start" | "end";
    rangeKey: string;
  } | null>(null);
  const rejectedField =
    rejection?.rangeKey === rangeKey(startDate, endDate)
      ? rejection.field
      : null;

  const applyRange = (
    field: "start" | "end",
    start: Date | null,
    end: Date | null
  ) => {
    if (start && end && end <= start) {
      setRejection({ field, rangeKey: rangeKey(startDate, endDate) });
      return;
    }
    setRejection(null);
    if (start && end) {
      onRangeChange(start, end);
    } else if (start) {
      onStartDateChange(start);
    } else if (end) {
      onEndDateChange(end);
    }
  };

  const handleStartSelect = (time?: string) => {
    if (!time) return;
    const start = injectTimeIntoDate(
      startDate ?? endDate ?? getNowDate(),
      time
    );
    applyRange("start", start, endDate);
  };

  const handleEndSelect = (time?: string) => {
    if (!time) return;
    const end = injectTimeIntoDate(endDate ?? startDate ?? getNowDate(), time);
    applyRange("end", startDate, end);
  };

  return (
    <div className="mb-2 grid grid-cols-2 gap-2 border-separator border-b pb-2">
      <TimeSelect
        label="Start"
        variant="outlined"
        value={startDate ? dateToTimeString(startDate) : undefined}
        onSelect={handleStartSelect}
        placeholder="Start time"
        error={rejectedField === "start" ? END_BEFORE_START_ERROR : undefined}
        triggerProps={{
          iconRight: <Clock className="h-4 w-4" />,
          className: "w-full px-3"
        }}
      />
      <TimeSelect
        label="End"
        variant="outlined"
        value={endDate ? dateToTimeString(endDate) : undefined}
        onSelect={handleEndSelect}
        placeholder="End time"
        error={rejectedField === "end" ? END_BEFORE_START_ERROR : undefined}
        triggerProps={{
          iconRight: <Clock className="h-4 w-4" />,
          className: "w-full px-3"
        }}
      />
    </div>
  );
};
